const abortError = () => {
  const error = new DOMException("Aborted", "AbortError");
  return error;
};

/**
 * Limits how many async tasks run at once (FIFO). Pass React Query's
 * `AbortSignal` so observers that unmount while waiting leave the queue
 * without ever hitting the network.
 */
export function createConcurrencyLimiter(maxConcurrent: number) {
  if (maxConcurrent < 1) {
    throw new Error("maxConcurrent must be at least 1");
  }

  let active = 0;
  const queue: Array<() => void> = [];

  const pump = () => {
    while (active < maxConcurrent && queue.length > 0) {
      const next = queue.shift();
      next?.();
    }
  };

  return function limit<T>(
    task: () => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    if (signal?.aborted) {
      return Promise.reject(abortError());
    }

    return new Promise<T>((resolve, reject) => {
      let queued = false;

      const onAbort = () => {
        if (!queued) return;
        const idx = queue.indexOf(run);
        if (idx >= 0) queue.splice(idx, 1);
        queued = false;
        signal?.removeEventListener("abort", onAbort);
        reject(abortError());
      };

      const run = () => {
        queued = false;
        signal?.removeEventListener("abort", onAbort);

        if (signal?.aborted) {
          reject(abortError());
          // Slot was never taken; continue draining the queue.
          pump();
          return;
        }

        active += 1;
        // Normalize sync throws so `active` is always released.
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            pump();
          });
      };

      signal?.addEventListener("abort", onAbort);

      if (active < maxConcurrent) {
        run();
      } else {
        queued = true;
        queue.push(run);
      }
    });
  };
}
